import React, { useState } from 'react';
import { Mail, Phone, MapPin, Building2, BadgePercent, Trash2, CheckCircle2, Clock, Sparkles, X, Reply, Copy, Check } from 'lucide-react';
import { buildReply } from './replyTemplate';
import { ContactNotes } from './ContactNotes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMeta = {
  new: { label: 'Neu', cls: 'bg-[#DC2626] text-white', I: Sparkles },
  in_progress: { label: 'In Bearbeitung', cls: 'bg-[#E0A72C] text-[#0B1E44]', I: Clock },
  done: { label: 'Erledigt', cls: 'bg-emerald-600 text-white', I: CheckCircle2 },
};

const fmtDate = (d) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function ContactCard({ contact: c, token, onStatus, onDelete, onNotes }) {
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(false);
  const meta = statusMeta[c.status] || statusMeta.new;
  const photoUrl = (p) => `${API}/files/${p}?auth=${encodeURIComponent(token)}`;
  const reply = buildReply(c);

  const copyReply = async () => {
    await navigator.clipboard.writeText(`Betreff: ${reply.subject}\n\n${reply.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <article className={`bg-white rounded-2xl border card-shadow p-5 md:p-6 ${c.status === 'new' ? 'border-[#DC2626]/40' : 'border-slate-100'}`} data-testid={`contact-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-extrabold text-[#0B1E44]">{c.name}</h2>
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.cls}`} data-testid="contact-status-badge">
              <meta.I className="w-3 h-3" /> {meta.label}
            </span>
            {c.promo_code && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#0B1E44] text-[#E8B547]" data-testid="contact-promo-badge">
                <BadgePercent className="w-3 h-3" /> {c.promo_code}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">{fmtDate(c.created_at)} · {c.service || 'Keine Leistung gewählt'}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={reply.mailto}
            onClick={() => { if (c.status === 'new') onStatus(c.id, 'in_progress'); }}
            className="h-10 px-4 rounded-lg btn-gold text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="contact-reply-mail"
            title="Antwort-E-Mail im Mailprogramm öffnen"
          >
            <Reply className="w-4 h-4" /> Angebot per Klick
          </a>
          <button onClick={copyReply} className="h-10 w-10 rounded-lg border border-slate-200 text-slate-500 hover:text-[#0B1E44] hover:border-[#B8892A] flex items-center justify-center transition-colors" aria-label="Antworttext kopieren" title="Antworttext kopieren" data-testid="contact-reply-copy">
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </button>
          <select value={c.status} onChange={(e) => onStatus(c.id, e.target.value)} className="h-10 px-3 rounded-lg bg-[#FAF7F2] border border-slate-200 text-sm text-[#0B1E44] focus:border-[#B8892A] outline-none" data-testid="contact-status-select">
            <option value="new">Neu</option>
            <option value="in_progress">In Bearbeitung</option>
            <option value="done">Erledigt</option>
          </select>
          {c.status !== 'done' && (
            <button onClick={() => onStatus(c.id, 'done')} className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors" data-testid="contact-mark-done">
              <CheckCircle2 className="w-4 h-4" /> Erledigt
            </button>
          )}
          <button onClick={() => onDelete(c.id)} className="h-10 w-10 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 flex items-center justify-center transition-colors" aria-label="Löschen" data-testid="contact-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-[#0B1E44] hover:text-[#DC2626] truncate"><Mail className="w-4 h-4 text-[#B8892A] shrink-0" /> {c.email}</a>
        {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 text-[#0B1E44] hover:text-[#DC2626]"><Phone className="w-4 h-4 text-[#B8892A] shrink-0" /> {c.phone}</a>}
        {c.company && <span className="inline-flex items-center gap-2 text-slate-600"><Building2 className="w-4 h-4 text-[#B8892A] shrink-0" /> {c.company}</span>}
        {c.location && <span className="inline-flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-[#B8892A] shrink-0" /> {c.location}</span>}
      </div>

      {c.message && <p className="mt-4 text-slate-700 text-sm leading-relaxed bg-[#FAF7F2] rounded-lg p-4 whitespace-pre-line" data-testid="contact-message-text">{c.message}</p>}

      {c.photos?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="contact-photos">
          {c.photos.map((p) => (
            <button key={p} onClick={() => setLightbox(p)} className="w-24 h-24 rounded-lg overflow-hidden border border-slate-200 hover:border-[#B8892A] transition-colors" data-testid="contact-photo-thumb">
              <img src={photoUrl(p)} alt="Kundenfoto" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <ContactNotes contact={c} onSave={onNotes} />

      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Schließen"><X className="w-5 h-5" /></button>
          <img src={photoUrl(lightbox)} alt="Kundenfoto groß" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </article>
  );
}
